import clsx from 'clsx';
import Image from 'next/image';
import { FC } from 'react';

import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Text } from '../common/Text';
import {
  WhySgCoverageBankTransferIcon,
  WhySgCoverageDigitalWalletIcon,
  WhySgCoverageLocalMethodsIcon,
  WhySgCoverageMajorCardsIcon,
} from '../whySg/icons/WhySgGlobalCoverageIcons';

import styles from './WhySgGlobalCoverageBlock.module.scss';

/** Map raster: `why_sg_global_coverage_map.png` (896×516). For extra-large columns / Retina, use a wider export if it ever looks soft. */
const MAP_SRC = '/assets/why_sg_global_coverage_map.png';

const FEATURE_CHIPS: Array<{ key: string; label: string; Icon: FC }> = [
  { key: 'cards', label: 'Major Cards', Icon: WhySgCoverageMajorCardsIcon },
  { key: 'wallets', label: 'Digital Wallets', Icon: WhySgCoverageDigitalWalletIcon },
  { key: 'bank', label: 'Bank Transfers', Icon: WhySgCoverageBankTransferIcon },
  { key: 'local', label: 'Local Methods', Icon: WhySgCoverageLocalMethodsIcon },
];

const PROVIDER_TAGS = ['VISA', 'Mastercard', 'PayPal', 'G Pay'] as const;

const STATS: Array<{ value: string; label: string }> = [
  { value: '100+', label: 'Countries' },
  { value: '300+', label: 'Payment Methods' },
  { value: '50+', label: 'Currencies' },
];

export const WhySgGlobalCoverageBlock: FC = () => {
  return (
    <Container className={styles.root}>
      <div className={styles.layout}>
        <div className={styles.contentRow}>
          <div className={styles.leftColumn}>
            <Flex direction={'column'} gap={4} className={styles.intro}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionLabelLine} aria-hidden />

                <Text type={'medium14'} color={'green'} className={styles.sectionEyebrow}>
                  Global coverage
                </Text>
              </div>

              <Text type={'regular48'} color={'white'} className={styles.title}>
                Global Reach, Local Presence
              </Text>

              <Text type={'regular16'} color={'white50'} whiteSpace={'normal'} className={styles.description}>
                Expand into new markets effortlessly. Support major card networks, digital wallets, and local payment
                methods across 100+ countries — all through a single integration.
              </Text>
            </Flex>

            <div className={styles.featureGrid} role="list">
              {FEATURE_CHIPS.map(({ key, label, Icon }, index) => (
                <div
                  key={key}
                  className={clsx(styles.featureChip, index === FEATURE_CHIPS.length - 1 && styles.featureChipSolo)}
                  role="listitem"
                >
                  <span className={styles.featureChipIcon}>
                    <Icon />
                  </span>
                  <Text type={'medium14'} color={'white'}>
                    {label}
                  </Text>
                </div>
              ))}
            </div>

            <Flex className={styles.statsRow} gap={10} wrap={'wrap'}>
              {STATS.map(({ value, label }) => (
                <div key={label} className={styles.statItem}>
                  <Text type={'regular48'} color={'green'} className={styles.statValue}>
                    {value}
                  </Text>
                  <Text type={'regular14'} color={'white50'}>
                    {label}
                  </Text>
                </div>
              ))}
            </Flex>
          </div>

          <div className={styles.visualColumn}>
            <div className={styles.mapFigure}>
              <Image
                className={styles.mapImg}
                src={MAP_SRC}
                width={896}
                height={516}
                alt="World map with coverage markers and Live in 100+ Countries — global payment footprint across continents."
                layout="responsive"
                sizes="(min-width: 960px) 50vw, 100vw"
              />
            </div>

            <div className={styles.providers}>
              <div className={styles.providersRow}>
                {PROVIDER_TAGS.map((name) => (
                  <span key={name} className={styles.providerTag}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
